import { Component, HostListener, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { AuthorizedApiService } from '../../services/authorized-api.service';
import { TranslateService } from '../../services/translate.service';
import { GuestWaitlistEntry, WaitlistAnswer } from '../../models/Order';
import { ActionButtonComponent } from '../buttons/action-button.component';
import { BorderIconButtonComponent } from '../buttons/border-icon-button.component';
import { LinkButtonComponent } from '../buttons/link-button.component';
import { LoadingSpinnerComponent } from '../ui/loading-spinner-component';
import { WAITLIST_ERRORS } from './waitlist-errors';

const POLL_MS = 20000;
// No more changes are expected for these, so polling stops
const FINAL_STATUSES = ['converted', 'cancelled', 'expired'];

// Where the waitlist SMS links land: /:venue_id/waitlist/:ref?t=token. Polls the entry and lets the guest answer.
@Component({
  selector: 'app-waitlist-status',
  standalone: true,
  imports: [CommonModule, ActionButtonComponent, BorderIconButtonComponent, LinkButtonComponent, LoadingSpinnerComponent],
  template: `
    <div class="max-w-lg mx-auto flex-col pt-20 text-theme-text">
      <div class="flex-1 bg-theme-surface-95 backdrop-blur-2xl rounded-t-3xl min-h-[calc(100vh-5rem)]">

        <div class="pt-6 px-8">
          <div class="font-bold text-xl">{{ getString('Väntelista') }}</div>
          @if (entry()?.venue_name) {
            <div class="text-theme-text2 mt-1">{{ entry()?.venue_name }}</div>
          }
        </div>

        <div class="px-8 pb-12">
          @if (isLoading()) {
            <div class="flex justify-center py-12">
              <app-loading-spinner size="lg"></app-loading-spinner>
            </div>
          } @else if (loadError()) {
            <div class="text-center pt-12">
              <i class="fas fa-circle-exclamation text-4xl text-theme-text3 mb-4"></i>
              <p class="text-theme-text2">{{ loadError() }}</p>
            </div>
          } @else {
            @if (entry(); as e) {

              @if (bookedRef()) {
                <!-- Notify: the guest just booked the offered time -->
                <div class="text-center pt-6 mb-6">
                  <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                    <i class="fas fa-check text-3xl text-green-600"></i>
                  </div>
                  <h2 class="text-xl font-bold">{{ getString('Bordet är bokat!') }}</h2>
                </div>
                <div class="bg-theme-surface2 rounded-2xl p-5 text-center mb-6">
                  <div class="text-sm text-theme-text2 mb-1">{{ getString('Bokningsreferens') }}</div>
                  <div class="text-2xl font-bold font-mono tracking-wider">{{ bookedRef() }}</div>
                </div>
                <p class="text-theme-text2 text-center">{{ getString('En bekräftelse på bokningen skickas till dig.') }}</p>
              } @else {

                @if (e.mode === 'queue') {
                  @switch (e.status) {
                    @case ('waiting') {
                      <div class="text-center pt-6">
                        <div class="text-theme-text2">{{ getString('Din plats i kön') }}</div>
                        <div class="text-6xl font-bold tabular-nums mt-2">{{ e.position ?? '-' }}</div>
                        @if (e.estimated_wait_minutes != null) {
                          <div class="text-lg mt-3">{{ waitText(e.estimated_wait_minutes) }}</div>
                        }
                      </div>
                      <div class="bg-theme-surface2 rounded-2xl p-5 mt-6 space-y-2 text-theme-text2">
                        <p>{{ getString('Du får ett SMS när ditt bord är klart. Håll dig i närheten.') }}</p>
                        <p>{{ partyText(e) }}</p>
                      </div>
                    }
                    @case ('offered') {
                      <div class="text-center pt-6">
                        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                          <i class="fas fa-bell text-3xl text-green-600"></i>
                        </div>
                        <h2 class="text-2xl font-bold">{{ getString('Ditt bord är klart!') }}</h2>
                        @if (countdown(); as c) {
                          <p class="text-theme-text2 mt-3">{{ getString('Svara inom') }}</p>
                          <div class="text-4xl font-bold tabular-nums mt-1">{{ c }}</div>
                        } @else {
                          <p class="text-theme-text2 mt-3">{{ getString('Svara så snart du kan.') }}</p>
                        }
                      </div>
                      <div class="mt-8 space-y-3">
                        <app-action-button
                          icon="fa-person-walking"
                          [text]="getString('Kommer')"
                          [chevron]="false"
                          [spinner]="isResponding()"
                          [disabled]="isResponding()"
                          (onClick)="respond('accept')">
                        </app-action-button>
                        <app-border-icon-button
                          icon="fa-xmark"
                          [text]="getString('Avstår')"
                          [disabled]="isResponding()"
                          (onClick)="respond('decline')">
                        </app-border-icon-button>
                      </div>
                    }
                    @case ('accepted') {
                      <div class="text-center pt-6">
                        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                          <i class="fas fa-door-open text-3xl text-green-600"></i>
                        </div>
                        <h2 class="text-2xl font-bold">{{ getString('Välkommen in!') }}</h2>
                      </div>
                    }
                    @case ('converted') {
                      @if (e.response === 'coming' && e.reservation_ref) {
                        <!-- The guest's yes made a reservation: shown at the door like any booking -->
                        <div class="text-center pt-6">
                          <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                            <i class="fas fa-check text-3xl text-green-600"></i>
                          </div>
                          <h2 class="text-2xl font-bold">{{ getString('Ditt bord är bokat!') }}</h2>
                          <p class="text-theme-text2 mt-3">{{ getString('Välkommen!') }}</p>
                        </div>
                        <div class="bg-theme-surface2 rounded-2xl p-5 mt-6 space-y-3">
                          <div class="text-center">
                            <div class="text-sm text-theme-text2 mb-1">{{ getString('Bokningsnummer') }}</div>
                            <div class="text-3xl font-bold font-mono tracking-wider">{{ e.reservation_ref }}</div>
                          </div>
                          <div class="flex justify-between items-center">
                            <span class="flex items-center gap-2 text-theme-text2">
                              <i class="fas fa-user text-xs w-4"></i>
                              <span>{{ getString('Antal') }}</span>
                            </span>
                            <span class="font-medium">{{ e.party_size }}</span>
                          </div>
                        </div>
                      } @else {
                        <div class="text-center pt-6">
                          <i class="fas fa-utensils text-4xl text-theme-text3 mb-4"></i>
                          <h2 class="text-xl font-bold">{{ getString('Du har fått ditt bord. Smaklig måltid!') }}</h2>
                        </div>
                      }
                    }
                    @default {
                      <div class="text-center pt-12">
                        <i class="fas fa-circle-info text-4xl text-theme-text3 mb-4"></i>
                        <p class="text-theme-text2">{{ endedText(e) }}</p>
                      </div>
                    }
                  }
                } @else {
                  @switch (e.status) {
                    @case ('waiting') {
                      <div class="text-center pt-6">
                        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-theme-surface2 mb-4">
                          <i class="fas fa-bell text-3xl text-theme-text2"></i>
                        </div>
                        <h2 class="text-xl font-bold">{{ getString('Du bevakar') }} {{ formatDate(e.date_key) }}</h2>
                      </div>
                      <div class="bg-theme-surface2 rounded-2xl p-5 mt-6 space-y-3">
                        <div class="flex justify-between items-center">
                          <span class="flex items-center gap-2 text-theme-text2">
                            <i class="fas fa-clock text-xs w-4"></i>
                            <span>{{ getString('Tid') }}</span>
                          </span>
                          <span class="font-medium">{{ e.time_from }} {{ getString('till') }} {{ e.time_to }}</span>
                        </div>
                        <div class="flex justify-between items-center">
                          <span class="flex items-center gap-2 text-theme-text2">
                            <i class="fas fa-user text-xs w-4"></i>
                            <span>{{ getString('Antal') }}</span>
                          </span>
                          <span class="font-medium">{{ e.party_size }}</span>
                        </div>
                      </div>
                      <p class="text-theme-text2 mt-6">
                        {{ getString('Vi skickar ett SMS om ett bord blir ledigt. Den som bokar först får bordet.') }}
                      </p>
                    }
                    @case ('offered') {
                      <div class="text-center pt-6">
                        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                          <i class="fas fa-bell text-3xl text-green-600"></i>
                        </div>
                        <h2 class="text-2xl font-bold">{{ getString('Ett bord är ledigt kl %s').replace('%s', e.offered_time ?? '') }}</h2>
                        <p class="text-theme-text2 mt-3 capitalize">{{ formatDate(e.date_key) }}</p>
                        <p class="text-theme-text2 mt-1">{{ partyText(e) }}</p>
                        <p class="text-theme-text2 mt-4">{{ getString('Den som bokar först får bordet.') }}</p>
                      </div>
                      <div class="mt-8">
                        <app-action-button
                          [text]="getString('Boka')"
                          [spinner]="isResponding()"
                          [disabled]="isResponding()"
                          (onClick)="respond('accept')">
                        </app-action-button>
                      </div>
                    }
                    @case ('converted') {
                      <div class="text-center pt-6">
                        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                          <i class="fas fa-check text-3xl text-green-600"></i>
                        </div>
                        <h2 class="text-xl font-bold">{{ getString('Bordet är bokat!') }}</h2>
                      </div>
                      @if (e.reservation_ref) {
                        <div class="bg-theme-surface2 rounded-2xl p-5 text-center mt-6">
                          <div class="text-sm text-theme-text2 mb-1">{{ getString('Bokningsreferens') }}</div>
                          <div class="text-2xl font-bold font-mono tracking-wider">{{ e.reservation_ref }}</div>
                          @if (e.reservation_time) {
                            <div class="text-theme-text2 mt-2 capitalize">{{ formatDate(e.date_key) }} {{ getString('kl') }} {{ e.reservation_time }}</div>
                          }
                        </div>
                      }
                    }
                    @default {
                      <div class="text-center pt-12">
                        <i class="fas fa-circle-info text-4xl text-theme-text3 mb-4"></i>
                        <p class="text-theme-text2">{{ endedText(e) }}</p>
                      </div>
                    }
                  }
                }

                @if (respondError()) {
                  <div class="mt-6 p-4 bg-red-100 border border-red-300 rounded-xl text-red-800 text-sm">
                    <div class="flex items-center gap-2">
                      <i class="fas fa-exclamation-circle"></i>
                      <span>{{ respondError() }}</span>
                    </div>
                  </div>
                }

                <!-- Leave: waiting/offered/accepted can be left -->
                @if (canLeave(e)) {
                  <div class="flex justify-center mt-10">
                    @if (confirmLeave()) {
                      <div class="text-center">
                        <p class="text-theme-text2 mb-3">{{ e.mode === 'queue' ? getString('Vill du lämna kön?') : getString('Vill du sluta bevaka?') }}</p>
                        <div class="flex gap-6 justify-center">
                          <app-link-button
                            color="text-red-600"
                            [text]="e.mode === 'queue' ? getString('Ja, lämna kön') : getString('Ja, sluta bevaka')"
                            [disabled]="isResponding()"
                            (onClick)="respond('leave')">
                          </app-link-button>
                          <app-link-button
                            font="font-medium"
                            [text]="getString('Nej')"
                            (onClick)="confirmLeave.set(false)">
                          </app-link-button>
                        </div>
                      </div>
                    } @else {
                      <app-link-button
                        font="font-medium"
                        [text]="e.mode === 'queue' ? getString('Lämna kön') : getString('Sluta bevaka')"
                        (onClick)="confirmLeave.set(true)">
                      </app-link-button>
                    }
                  </div>
                }
              }
            }
          }
        </div>
      </div>
    </div>
  `
})
export class WaitlistStatusComponent implements OnInit, OnDestroy {
  private api = inject(AuthorizedApiService);
  private route = inject(ActivatedRoute);
  private translate = inject(TranslateService);

  private venueId = '';
  private ref = '';
  private token = '';
  private pollTimer?: ReturnType<typeof setInterval>;
  private clockTimer?: ReturnType<typeof setInterval>;
  private fetching = false;

  entry = signal<GuestWaitlistEntry | undefined>(undefined);
  isLoading = signal(true);
  loadError = signal<string | undefined>(undefined);
  isResponding = signal(false);
  respondError = signal<string | undefined>(undefined);
  bookedRef = signal<string | undefined>(undefined);
  confirmLeave = signal(false);
  private now = signal(Date.now());

  // mm:ss left to answer a queue offer, undefined when there is no deadline (or it has passed)
  countdown = computed(() => {
    const expires = this.entry()?.offer_expires_at;
    if (!expires) return undefined;
    const left = Math.floor((Date.parse(expires) - this.now()) / 1000);
    if (isNaN(left) || left <= 0) return undefined;
    return `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
  });

  getString(s: string) {
    return this.translate.getString(s);
  }

  ngOnInit() {
    this.venueId = this.route.snapshot.paramMap.get('venue_id') ?? '';
    this.ref = this.route.snapshot.paramMap.get('ref') ?? '';
    this.token = this.route.snapshot.queryParamMap.get('t') ?? '';
    if (!this.ref || !this.token) {
      this.isLoading.set(false);
      this.loadError.set(this.getString('Länken är ofullständig. Öppna länken i ditt SMS igen.'));
      return;
    }
    this.refresh();
    this.pollTimer = setInterval(() => this.refresh(), POLL_MS);
    this.clockTimer = setInterval(() => this.now.set(Date.now()), 1000);
  }

  ngOnDestroy() {
    clearInterval(this.pollTimer);
    clearInterval(this.clockTimer);
  }

  @HostListener('window:focus')
  @HostListener('document:visibilitychange')
  onFocus() {
    if (document.visibilityState === 'visible') {
      this.refresh();
    }
  }

  private refresh() {
    const status = this.entry()?.status;
    if (this.fetching || !this.ref || (status && FINAL_STATUSES.includes(status))) return;
    this.fetching = true;
    this.api.waitlistStatus(this.venueId, this.ref, this.token).subscribe({
      next: (response) => {
        this.fetching = false;
        this.isLoading.set(false);
        if (response.success && response.entry) {
          this.setEntry(response.entry);
          this.loadError.set(undefined);
        } else if (!this.entry()) {
          this.loadError.set(this.getString(WAITLIST_ERRORS[response.error ?? ''] || 'Vi hittar inte din plats i väntelistan. Kontrollera länken.'));
        }
      },
      error: () => {
        this.fetching = false;
        this.isLoading.set(false);
        if (!this.entry()) {
          this.loadError.set(this.getString('Det gick inte att hämta väntelistan. Försök igen om en stund.'));
        }
      }
    });
  }

  private setEntry(entry: GuestWaitlistEntry) {
    if (entry.status !== this.entry()?.status) {
      this.confirmLeave.set(false);
    }
    this.entry.set(entry);
  }

  respond(answer: WaitlistAnswer) {
    this.isResponding.set(true);
    this.respondError.set(undefined);
    this.api.waitlistRespond(this.venueId, this.ref, this.token, answer).subscribe({
      next: (response) => {
        this.isResponding.set(false);
        if (response.entry) {
          this.setEntry(response.entry);
        }
        if (response.success) {
          // A queued guest's yes shows as the converted entry (booking ref; never the table, the host seats them)
          if (response.booking_ref && response.entry?.mode === 'notify') {
            this.bookedRef.set(response.booking_ref);
          }
        } else {
          const errorKey = response.error || 'unknown';
          this.respondError.set(this.getString(WAITLIST_ERRORS[errorKey] || 'Något gick fel. Försök igen.'));
          if (!response.entry) {
            this.refresh();
          }
        }
      },
      error: () => {
        this.isResponding.set(false);
        this.respondError.set(this.getString('Något gick fel. Försök igen.'));
      }
    });
  }

  canLeave(e: GuestWaitlistEntry): boolean {
    return e.status === 'waiting' || e.status === 'offered' || e.status === 'accepted';
  }

  waitText(minutes: number): string {
    if (minutes <= 0) return this.getString('Snart din tur');
    return this.getString('Ca %s min').replace('%s', String(minutes));
  }

  partyText(e: GuestWaitlistEntry): string {
    if (e.party_size === 1) return this.getString('Bord för 1 person');
    return this.getString('Bord för %s personer').replace('%s', String(e.party_size));
  }

  endedText(e: GuestWaitlistEntry): string {
    const queue = e.mode === 'queue';
    switch (e.status) {
      case 'cancelled':
        return queue ? this.getString('Du står inte längre i kön.') : this.getString('Bevakningen är avslutad.');
      case 'expired':
        return queue ? this.getString('Kön för dagen är stängd.') : this.getString('Bevakningen har gått ut.');
      case 'no_answer':
        return this.getString('Vi fick inget svar i tid, så bordet gick vidare.');
      case 'declined':
        return this.getString('Du tackade nej till bordet.');
      default:
        return '';
    }
  }

  formatDate(dateKey?: string): string {
    if (!dateKey) return '';
    const date = new Date(dateKey);
    return date.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' });
  }
}
